/**
 * `<ActivityCheckpointMessage>` — inline build checkpoint row rendered
 * between chat bubbles (Crucible #602 umbrella, #738 Issue D — C4
 * inline checkpoints). Source: `docs/c4-inline-checkpoints-design.md`
 * §5 (visual spec + per-trigger render variants + accessibility) and
 * §9 (component contract + memoisation).
 *
 * Architectural shape (design §9):
 *
 *   - Pure render. No state, no effects, no subscriptions — every
 *     prop arrives via the chat reducer. The component is mounted by
 *     `ChatBubble` (Workspace.tsx) inside the `message.role ===
 *     'inline-checkpoint'` discriminator branch. Issue E wires that
 *     branch; this component is the leaf it points at.
 *
 *   - `React.memo` with a strict per-field comparator (§9). Since
 *     checkpoints are durable artefacts and never mutate after first
 *     append, the comparator always returns `true` for a given
 *     `(message-id, checkpoint-id)` and the component renders exactly
 *     once per checkpoint ever loaded. Render budget = 0 cycles after
 *     first paint.
 *
 *   - Defence-in-depth flag-gate: the component reads
 *     `useFeatureFlags()` and renders `null` when `!flags.loaded ||
 *     !flags.agentTransparency`. The parent (E — ChatBubble) decides
 *     whether to mount; this gate stops a stray push from rendering
 *     a visible row if some future caller mounts the component
 *     directly without the upstream gate. Mirrors the same pattern
 *     as `<ActivityBanner>` and `<MainAgentRowCard>`.
 *
 *   - No animation — design §5 is explicit: "no animation regardless".
 *     Checkpoints are static blocks. `prefers-reduced-motion: reduce`
 *     is trivially honoured (nothing to suppress).
 *
 *   - Not interactive — `cursor-default`, no hover, no focus stop.
 *     Design §5 ("Contract A") forbids expandable checkpoints; the
 *     verbose-mode escape hatch is the C2 banner drawer (Contract B).
 *
 * British spelling baked into the static strings — `behaviour`,
 * `centre`, `colour`.
 */

import React, { memo } from 'react';
import type { InlineCheckpoint } from '@volley/crucible-ipc-types/ipc-channels';
import {
  CHECKPOINT_FALLBACK_ACTION,
  CHECKPOINT_FALLBACK_SPECIALIST,
  CHECKPOINT_FALLBACK_SUBSYSTEM,
  denormaliseCheckpointContent,
  progressPhaseTransitionHeadline,
} from '@volley/crucible-ipc-types/checkpoint-content';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

// ---------------------------------------------------------------------------
// Microcopy constants (design §5 — exported for pinned-table tests).
// ---------------------------------------------------------------------------

/** Headline strings per `trigger` value (single-specialist variant for
 *  `specialist-dispatch` — coalesced variants append a comma list). */
export const CHECKPOINT_HEADLINE_SINGLE_SPECIALIST = 'Specialist dispatched';
export const CHECKPOINT_HEADLINE_MULTI_SPECIALIST = 'Specialists dispatched';
/** Fallback subsystem label when the checkpoint records `subsystem: null`
 *  (rare — emitter usually has a subsystem in scope, but the wire
 *  permits `null`). Mirrors the emitter's fallback so the chat-history
 *  restore and the live render show identical copy (Crucible #785).
 *  British spelling. */
export const SUBSYSTEM_FALLBACK = CHECKPOINT_FALLBACK_SUBSYSTEM;
/** Fallback action label for the same window — mirrors the emitter's
 *  fallback (Crucible #785). */
export const ACTION_FALLBACK = CHECKPOINT_FALLBACK_ACTION;
/** Fallback specialist label for `specialist-dispatch` rows whose
 *  optional `specialist` wire field is absent. Mirrors the shared
 *  fallback so renderer + emitter stay in sync (Bugbot round-1 medium). */
export const SPECIALIST_FALLBACK = CHECKPOINT_FALLBACK_SPECIALIST;

// Re-export the shared denormaliser so tests + downstream callers can
// import the single source of truth via the component module.
export { denormaliseCheckpointContent };

// ---------------------------------------------------------------------------
// Pure helpers — co-located so the tests can exercise them in isolation.
// ---------------------------------------------------------------------------

/**
 * Format a UNIX-ms timestamp as `HH:MM` in the local time zone (24h
 * clock). Reuses `Intl.DateTimeFormat` for locale-correct digits +
 * separator without pulling a date library; tests pin the output for
 * a fixed reference timestamp.
 *
 * Note: design §5 shows `14:32` — 24h, colon-separated, no seconds.
 * We force `hourCycle: 'h23'` so the `en-GB` locale renders `00:00`
 * not `24:00` and never falls back to a 12h clock.
 */
export function formatCheckpointTimestamp(timestampMs: number): string {
  const date = new Date(timestampMs);
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

/**
 * Compute the headline string for a checkpoint per design §5
 * per-trigger render table.
 *
 *   `phase-transition`    → `<build-flow phase> begins`
 *   `specialist-dispatch` → `Specialist dispatched` (1) /
 *                           `Specialists dispatched` (2+)
 *   `subsystem-switch`    → `Now: <subsystem>`
 *   `time-cap`            → `Still on <subsystem>`
 *
 * `phase` / `subsystem` can be `null` on the wire. Defensive fallbacks
 * keep the row legible even when an upstream emitter forgot to fill
 * a slot — we never crash, we degrade.
 */
export function computeHeadline(checkpoint: InlineCheckpoint): string {
  switch (checkpoint.trigger) {
    case 'phase-transition': {
      // `phase` is the 1-7 phase number per the wire contract.
      // Null `phase` shouldn't happen for this trigger but we
      // fall back to phase 1 to stay aligned with the emitter's
      // `denormaliseCheckpointContent` (Crucible #785). Pinned
      // by the pinned-table test that asserts headline + body
      // composition matches the on-disk content.
      return progressPhaseTransitionHeadline(checkpoint.phase);
    }
    case 'specialist-dispatch': {
      // Single vs multi distinguished by whether the `specialist`
      // field carries a comma (coalesced renderer convention —
      // the emitter joins agentTypes with `, ` when 2+ landed
      // inside the 10s window). An absent field falls back to
      // the shared `SPECIALIST_FALLBACK` so the row matches
      // `denormaliseCheckpointContent` (Bugbot round-1 medium).
      const list = checkpoint.specialist ?? SPECIALIST_FALLBACK;
      const hasMultiple = list.includes(',');
      return hasMultiple
        ? CHECKPOINT_HEADLINE_MULTI_SPECIALIST
        : CHECKPOINT_HEADLINE_SINGLE_SPECIALIST;
    }
    case 'subsystem-switch': {
      return `Now: ${checkpoint.subsystem ?? SUBSYSTEM_FALLBACK}`;
    }
    case 'time-cap': {
      return `Still on ${checkpoint.subsystem ?? SUBSYSTEM_FALLBACK}`;
    }
    default: {
      // Exhaustiveness guard — TS narrows the switch above to
      // never. If a future wire revision adds a trigger, this
      // branch keeps the renderer compiling while degrading
      // legibly until the design catches up.
      const _exhaustive: never = checkpoint.trigger;
      void _exhaustive;
      return 'Build checkpoint';
    }
  }
}

/**
 * Compute the body line(s) for a checkpoint per design §5 per-trigger
 * render table. Returns a tuple of `{ subtitle, specialistInline }`:
 *
 *   - `subtitle` is the bulk body text — always rendered.
 *   - `specialistInline` carries the cyan-accented agent name(s) for
 *     the `specialist-dispatch` variant; `null` for every other
 *     trigger. Renderer paints it as a separate <span> so the
 *     `text-cyan-300/80` class applies only to the name, not the
 *     surrounding body.
 *
 * The renderer composes these into a final body row that screen
 * readers concatenate naturally (no visual-only separator characters
 * that would leak into the aria-label).
 */
export function computeBody(checkpoint: InlineCheckpoint): {
  subtitle: string;
  specialistInline: string | null;
} {
  switch (checkpoint.trigger) {
    case 'phase-transition': {
      // `<subsystem> — <action>` per §5 / `denormaliseCheckpointContent`.
      // Wire fields are surfaced verbatim; nulls fall back to the
      // shared "Starting up" / "Reading project context" labels
      // so the row stays legible when the registry's marker
      // parser couldn't fill a slot (Crucible #785 — previously
      // dropped both fields entirely, rendered as bare
      // `<build-flow phase> begins`).
      const subsystem = checkpoint.subsystem ?? SUBSYSTEM_FALLBACK;
      const action = checkpoint.action ?? ACTION_FALLBACK;
      return { subtitle: `${subsystem} — ${action}`, specialistInline: null };
    }
    case 'specialist-dispatch': {
      // `<agent-list> · <subsystem>` per §5. The agent list is
      // the inline cyan accent; the subsystem trails after the
      // middle-dot separator. We split the body so the colour
      // only paints the names. An absent `specialist` wire
      // field falls back to the shared `SPECIALIST_FALLBACK`
      // string so the rendered row matches
      // `denormaliseCheckpointContent` (Bugbot round-1 medium).
      const specialistInline = checkpoint.specialist ?? SPECIALIST_FALLBACK;
      const subsystem = checkpoint.subsystem ?? SUBSYSTEM_FALLBACK;
      return { subtitle: subsystem, specialistInline };
    }
    // `subsystem-switch` and `time-cap` render identically: the body is
    // the (pre-formatted) action, headline carries the rest.
    // - subsystem-switch: body is the action (e.g. "Writing the
    //   reducer"); headline carries the subsystem — see `computeHeadline`.
    // - time-cap: the emitter pre-formats the elapsed string into
    //   `action` (e.g. "5m in · Drafting layout") so the renderer is
    //   plain-text.
    // Both fall back to the shared "Reading project context" label on a
    // null action, matching `denormaliseCheckpointContent`.
    case 'subsystem-switch':
    case 'time-cap': {
      const subtitle = checkpoint.action ?? ACTION_FALLBACK;
      return { subtitle, specialistInline: null };
    }
    default: {
      const _exhaustive: never = checkpoint.trigger;
      void _exhaustive;
      return { subtitle: '', specialistInline: null };
    }
  }
}

/**
 * Compose the full visible-text rendering of a checkpoint —
 * `headline + " — " + body` (matching `denormaliseCheckpointContent`).
 *
 * The renderer paints the headline on one line and the body line(s)
 * underneath, but the **textual content** of those rows joined with
 * the natural " — " separator equals the on-disk denormalised string.
 * Pinned-table tests use this helper to assert visible-text parity
 * with the chat-history.json record (Crucible #785).
 *
 * Special case: `specialist-dispatch` splits the body into
 * `specialistInline · subtitle`, so we join with " · " between them
 * to match the denormalised shape (`Specialist dispatched —
 * <specialist> · <subsystem>`).
 */
export function composeCheckpointText(checkpoint: InlineCheckpoint): string {
  const headline = computeHeadline(checkpoint);
  const body = computeBody(checkpoint);
  const bodyText =
    body.specialistInline !== null && body.specialistInline.length > 0
      ? body.subtitle.length > 0
        ? `${body.specialistInline} · ${body.subtitle}`
        : body.specialistInline
      : body.subtitle;
  if (bodyText.length === 0) return headline;
  return `${headline} — ${bodyText}`;
}

/**
 * Compose the screen-reader subtitle (the trailing prose of the aria-
 * label). For most triggers the body is a single string and we use it
 * verbatim — but `specialist-dispatch` splits the visual body across
 * two spans (cyan agent names + plain subsystem) so a naive
 * `body.subtitle` read drops the most important slot (the agent
 * name(s)). Codex round-1 P2 caught this: SR users would hear
 * "Specialist dispatched, Lobby component" and miss the dispatched
 * agent names that sighted users see. We fix it by joining the
 * specialist list and the subsystem with " · " for the SR string so
 * the announcement matches the visual row.
 */
export function computeAriaSubtitle(body: {
  subtitle: string;
  specialistInline: string | null;
}): string {
  const specialist = body.specialistInline ?? '';
  if (specialist.length === 0) return body.subtitle;
  if (body.subtitle.length === 0) return specialist;
  return `${specialist} · ${body.subtitle}`;
}

/**
 * Build the screen-reader aria-label that the outer article advertises.
 * Per design §5 Accessibility — `timestamp + " — " + headline + ", " +
 * <subtitle>` so a screen reader hears the moment at a glance without
 * navigating into the article. The subtitle MUST include the
 * specialist names on `specialist-dispatch` rows; see
 * `computeAriaSubtitle` for the rationale.
 */
export function computeAriaLabel(
  timestampLabel: string,
  headline: string,
  subtitle: string,
): string {
  if (subtitle.length === 0) {
    return `Build checkpoint at ${timestampLabel} — ${headline}`;
  }
  return `Build checkpoint at ${timestampLabel} — ${headline}, ${subtitle}`;
}

// ---------------------------------------------------------------------------
// Props + comparator
// ---------------------------------------------------------------------------

export interface ActivityCheckpointMessageProps {
  /** The wire-shape checkpoint payload (frozen — see C1 wire). */
  readonly checkpoint: InlineCheckpoint;
  /** The chat-message-level timestamp (UNIX ms). Independently
   *  authoritative vs the checkpoint's own `timestamp`; design §9
   *  picks the message-level for consistency with neighbouring
   *  bubbles. */
  readonly timestamp: number;
}

/**
 * Memo comparator (design §9). Returns `true` when the row can SKIP
 * a re-render. Audits every field the renderer reads. The wire is
 * FROZEN so this list is closed — a future field addition would also
 * need a comparator entry.
 *
 * `checkpoint.timestamp` is intentionally NOT in the comparator:
 * the renderer reads `props.timestamp` (the message-level one), per
 * §9.
 */
function checkpointPropsEqual(
  prev: ActivityCheckpointMessageProps,
  next: ActivityCheckpointMessageProps,
): boolean {
  if (prev.timestamp !== next.timestamp) return false;
  if (prev.checkpoint.id !== next.checkpoint.id) return false;
  if (prev.checkpoint.trigger !== next.checkpoint.trigger) return false;
  if (prev.checkpoint.phase !== next.checkpoint.phase) return false;
  if (prev.checkpoint.subsystem !== next.checkpoint.subsystem) return false;
  if (prev.checkpoint.action !== next.checkpoint.action) return false;
  if (prev.checkpoint.specialist !== next.checkpoint.specialist) return false;
  return true;
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

function ActivityCheckpointMessageImpl({
  checkpoint,
  timestamp,
}: ActivityCheckpointMessageProps): React.JSX.Element {
  const timestampLabel = formatCheckpointTimestamp(timestamp);
  const headline = computeHeadline(checkpoint);
  const body = computeBody(checkpoint);
  const ariaSubtitle = computeAriaSubtitle(body);
  const ariaLabel = computeAriaLabel(timestampLabel, headline, ariaSubtitle);

  // Body composition — the cyan specialist accent paints just the
  // names, with a `text-white/55` middle-dot + subsystem trailing
  // after. When the trigger is not `specialist-dispatch`, the body
  // is a single plain `text-white/55` span.
  const bodyNode =
    body.specialistInline !== null && body.specialistInline.length > 0 ? (
      <p className="text-xs text-white/55" data-testid="activity-checkpoint-body">
        <span className="text-cyan-300/80" data-testid="activity-checkpoint-specialist">
          {body.specialistInline}
        </span>
        {body.subtitle.length > 0 ? (
          <>
            <span
              className="text-white/40"
              aria-hidden="true"
              data-testid="activity-checkpoint-body-separator"
            >
              {' · '}
            </span>
            <span data-testid="activity-checkpoint-subsystem">{body.subtitle}</span>
          </>
        ) : null}
      </p>
    ) : (
      <p className="text-xs text-white/55" data-testid="activity-checkpoint-body">
        <span data-testid="activity-checkpoint-subsystem">{body.subtitle}</span>
      </p>
    );

  return (
    // Per design §5 Accessibility: each checkpoint is announced as a
    // self-contained article. We use the native `<article>` element
    // because `<section role="article">` (a verbatim reading of the
    // design's notation) trips axe's `aria-allowed-role` rule —
    // `role="article"` is not allowed on `<section>`. The native
    // element delivers the same semantics for screen readers
    // without the override.
    <article
      aria-label={ariaLabel}
      data-testid="activity-checkpoint"
      data-checkpoint-id={checkpoint.id}
      data-trigger={checkpoint.trigger}
      className="my-2 cursor-default rounded border border-white/5 bg-white/[0.03] px-3 py-2"
    >
      <div className="flex items-baseline gap-2">
        {/* Diamond glyph — milestone metaphor, amber-tinted.
                    aria-hidden because the aria-label already carries
                    every semantic the glyph could convey. */}
        <span
          aria-hidden="true"
          data-testid="activity-checkpoint-glyph"
          className="text-[14px] leading-none text-amber-300/70"
        >
          ◆
        </span>
        <span
          className="font-mono text-[10px] tabular-nums text-white/40"
          data-testid="activity-checkpoint-timestamp"
        >
          {timestampLabel}
        </span>
        <span
          className="text-white/40"
          aria-hidden="true"
          data-testid="activity-checkpoint-separator"
        >
          ·
        </span>
        <span
          className="text-xs font-medium text-white/85"
          data-testid="activity-checkpoint-headline"
        >
          {headline}
        </span>
      </div>
      <div className="ml-6 mt-0.5">{bodyNode}</div>
    </article>
  );
}

const ActivityCheckpointMessageView = memo(ActivityCheckpointMessageImpl, checkpointPropsEqual);
ActivityCheckpointMessageView.displayName = 'ActivityCheckpointMessageView';

// ---------------------------------------------------------------------------
// Flag-gated wrapper
// ---------------------------------------------------------------------------

/**
 * Flag-gated entry point. Defence-in-depth — the parent (Issue E
 * `ChatBubble` discriminator branch) is the canonical mount-gate but
 * this hook makes the component safe to mount in isolation. Returns
 * `null` until the flag query has resolved AND
 * `agentTransparency === true`.
 */
export function ActivityCheckpointMessage(
  props: ActivityCheckpointMessageProps,
): React.JSX.Element | null {
  const flags = useFeatureFlags();
  if (!flags.loaded || !flags.agentTransparency) return null;
  return <ActivityCheckpointMessageView {...props} />;
}
ActivityCheckpointMessage.displayName = 'ActivityCheckpointMessage';

export {
  ActivityCheckpointMessageView,
  checkpointPropsEqual as activityCheckpointMessagePropsEqual,
};
